import {
  Block,
  Moment,
  Text,
  type CriterionOutcome,
  type Projection,
  type VerwijzingOutcome,
} from '@eformulieren/model'
import type { EarlierVersie, FormProps, VersionMoment } from '@eformulieren/renderer'
import { z } from 'zod'
import { isAdminRequest } from './admin.js'
import { type AppearanceBasis } from './appearance.js'
import type { FormContext } from './formContext.js'
import { logRejection } from './rejection.js'
import { SessionExpiredError, isSessionExpiredResponse } from './session.js'

/**
 * De verzoeken van de formulierpagina aan de pod: de koppen, en het lezen van de projectie en van wat
 * er naast het formulier staat (R-176, R-174, R-97, R-125). Tot deze opsplitsing stond dit in
 * `FormPage.tsx`; de handelingen zelf (indienen, een oordeel) staan daar nog, bij de stand die ze zetten.
 */

/**
 * De CSRF-header bij elke schrijfactie met het cookie. De waarde doet niet mee, alleen de aanwezigheid:
 * een cross-site verzoek kán deze header niet zetten (`CSRF_HEADER` in `src/cookie.ts` van de pod).
 * Tot deze opdracht stond hij met de hand in acht verzoeken in vijf bestanden.
 */
export const CLIENT_HEADER = { 'x-eformulieren-client': 'pagina' } as const

/** De koppen van een schrijfactie met een JSON-lijf: de CSRF-header en het type. */
export const WRITE_HEADERS = { ...CLIENT_HEADER, 'content-type': 'application/json' } as const

/**
 * De pod zegt dat deze kijker geen toekenning heeft maar als functioneel beheerder kan openen
 * (R-155f, `beheer.ts`). Geen fout van het formulier maar een vraag: de pagina toont haar in plaats
 * van "het formulier kwam niet op".
 */
export class AdminQueryError extends Error {
  constructor() {
    super('GET /projectie: geen toekenning, openen als beheerder kan')
    this.name = 'Beheervraagfout'
  }
}

/** De pod zegt 404 op `/projectie`: het formulier bestaat niet, of niet voor deze persoon. */
export class NoAccessError extends Error {
  constructor() {
    super('geen toegang tot dit formulier')
    this.name = 'NoAccessError'
  }
}

/**
 * De koppen voor een verzoek aan de pod: een token als de gastheer er een gaf, en anders niets.
 *
 * Niets is hier het juiste antwoord en geen omissie: zonder header valt de pod terug op het
 * sessiecookie, en dat is de weg van de directe link.
 *
 * **Wat er misging was `Bearer undefined` en niet een lege `Bearer`**, en dat verschil is bij
 * M5.16f rechtgezet omdat het hier eerst verkeerd stond. `tokenUitHeader` matcht
 * `/^Bearer (.+)$/` op de getrimde header, dus `Bearer ` levert `undefined` op en dan wordt het
 * cookie juist wél gelezen. Wat de vorige versie stuurde was `Bearer ${context.token}` met
 * `context.token === undefined`, dus de letterlijke tekst `Bearer undefined`; die matcht wel, en
 * dan wint de header van het cookie en valt de validatie om op een token dat niet bestaat.
 */
export function headersFor(context: FormContext): Record<string, string> {
  return context.token === undefined ? {} : { authorization: `Bearer ${context.token}` }
}

/** Wat `GET /projectie` levert: de projectie, haar verwijzingen, en de grond voor de gedaante. */
export interface ProjectionResponse {
  readonly projectie: Projection
  readonly verwijzingen: Readonly<Record<string, VerwijzingOutcome>>
  readonly grond: AppearanceBasis
  /** Tot wanneer een opening door beheer loopt, als de kijker alleen zo kijkt (R-155f). */
  readonly beheerTot: string | undefined
}

export async function getProjection(context: FormContext): Promise<ProjectionResponse> {
  // Een absoluut pad op de eigen oorsprong: de pod serveert deze pagina zelf (11.1).
  const url = `/projectie?instantie=${encodeURIComponent(context.instantieId)}`
  const response = await fetch(url, { headers: headersFor(context) })
  if (!response.ok) {
    const text = await response.text()
    // Een beheerder zonder toekenning krijgt de vraag om als beheerder te kijken (R-155f).
    if (isAdminRequest(response.status, text)) throw new AdminQueryError()
    // Een verlopen sessie is geen formulier dat niet opkwam, maar een reden om opnieuw in te loggen
    // (`session.ts`); dan geen ruwe status en geen lijf op het scherm.
    if (isSessionExpiredResponse(response.status)) throw new SessionExpiredError()
    // Geen toegang geeft hetzelfde antwoord als niet gevonden (9.2 regel 1): dan een eigen pagina
    // en geen ruw antwoord (audit delen van 30 oktober 2026). De volledige keten gaat naar het log.
    if (response.status === 404) {
      logRejection({ methode: 'GET', pad: url, status: response.status, lijf: text.slice(0, 2000) })
      throw new NoAccessError()
    }
    throw new Error(`GET /projectie gaf ${response.status}: ${text.slice(0, 200)}`)
  }
  // De verwijzingen komen als aparte sleutel naast de projectie mee (M6.3): `Projectie` draagt
  // met opzet geen waarden, en een tweede aanroep zou de instantie, de delingen en de stand
  // opnieuw laten ophalen. Zie `getForm` in `apps/runtime/src/projectie.ts`.
  //
  // `instantie` staat er sinds deze opdracht ook naast (`server.ts`): open of bevroren, en of er
  // een voorganger is. Samen met `actieveLaag`, `rol` en `kant` uit de projectie is dat alles wat
  // de gedaante nodig heeft (`gedaante.ts`), uit dit ene verzoek dat toch al fataal is.
  const body = (await response.json()) as Projection & {
    verwijzingen?: Readonly<Record<string, VerwijzingOutcome>>
    instantie?: { status?: unknown; heeftVoorganger?: unknown }
    beheeropening?: { tot?: unknown }
  }
  // `beheeropening` uit de rest, zodat het niet als veld van de projectie naar de renderer reist.
  const { verwijzingen, instantie, beheeropening: adminOpening, ...projection } = body
  if (
    (instantie?.status !== 'open' && instantie?.status !== 'bevroren') ||
    typeof instantie.heeftVoorganger !== 'boolean'
  ) {
    // Een pod zonder deze sleutel is een pod van vóór deze opdracht, en die kan deze pagina niet
    // serveren: pagina en pod komen uit hetzelfde image (R-101). Dan klopt er iets niet, en dat
    // hoort te zeggen wat, in plaats van stil op een gedaante terug te vallen.
    throw new Error('GET /projectie gaf geen geldige instantiestand mee')
  }
  return {
    projectie: projection,
    verwijzingen: verwijzingen ?? {},
    grond: {
      status: instantie.status,
      heeftVoorganger: instantie.heeftVoorganger,
      actieveLaag: projection.actieveLaag,
      rol: projection.rol,
      kant: projection.kant,
      magIndienen: projection.magIndienen,
      beheer: projection.beheer,
    },
    beheerTot: typeof adminOpening?.tot === 'string' ? adminOpening.tot : undefined,
  }
}

/**
 * Een leesverzoek dat niet fataal is: bij een weigering, een `fetch` die gooit of een lijf dat niet
 * te lezen is komt de terugval terug, en het formulier staat er gewoon (zie de lezers hieronder).
 * `read` draait binnen dezelfde vangst, zodat een lijf in een onverwachte vorm ook de terugval geeft.
 */
async function readOptional<T>(context: FormContext, url: string, fallback: T, read: (body: unknown) => T): Promise<T> {
  try {
    const response = await fetch(url, { headers: headersFor(context) })
    if (!response.ok) return fallback
    return read(await response.json())
  } catch {
    return fallback
  }
}

/**
 * Wat deze persoon bij deze overdracht moet bekijken (R-176), of niets.
 *
 * Niet fataal als hij niet komt. Een gastheer die de werkverzameling niet ophaalt hoort het
 * formulier gewoon te zien; dat is precies de val-terug die het skelet biedt (geen band, geen
 * badges, alles open). Een harde fout hier zou een pagina onbruikbaar maken voor iets dat een
 * toevoeging is.
 */
export async function getWerkverzameling(
  context: FormContext,
): Promise<WerkverzamelingBody | undefined> {
  const url = `/werkverzameling?instantie=${encodeURIComponent(context.instantieId)}`
  return await readOptional<WerkverzamelingBody | undefined>(context, url, undefined, (body) => body as WerkverzamelingBody)
}

/**
 * De versielijst van deze aanvraag (R-174), of niets.
 *
 * Niet fataal als hij niet komt, om dezelfde reden als bij de werkverzameling: het formulier
 * hoort er te staan ook als een toevoeging niet lukt. Zonder lijst is er geen kiezer, en dat is
 * precies de stand van een eerste beurt.
 */
export async function getVersies(context: FormContext): Promise<readonly EarlierVersie[]> {
  const url = `/revisies?instantie=${encodeURIComponent(context.instantieId)}`
  return await readOptional<readonly EarlierVersie[]>(context, url, [], (body) => (body as { revisies?: readonly EarlierVersie[] }).revisies ?? [])
}

/**
 * De standen van deze beurt voor het paneel Geschiedenis (R-97, R-188, ontwerp 6.9), of niets.
 *
 * Niet fataal, om dezelfde reden als de versielijst: zonder standen staat Geschiedenis er alleen met de
 * eerder ingediende versies. De pod levert per sectie de snapshot als base64 en groepeert per moment;
 * een moment heeft geen eigen id, dus de soort en het moment samen zijn de sleutel.
 */
export async function getVersiemomenten(context: FormContext): Promise<readonly VersionMoment[] | undefined> {
  const url = `/versiemomenten?instantie=${encodeURIComponent(context.instantieId)}`
  return await readOptional<readonly VersionMoment[] | undefined>(context, url, undefined, (read) => {
    const body = read as {
      versiemomenten?: readonly {
        readonly moment: string
        readonly soort: 'begin' | 'automatisch'
        readonly auteurs: VersionMoment['auteurs']
        readonly secties: readonly { readonly sectieId: string; readonly snapshot: string }[]
      }[]
    }
    return (body.versiemomenten ?? []).map((row) => ({
      id: `${row.soort}:${row.moment}`,
      soort: row.soort,
      moment: row.moment,
      auteurs: row.auteurs,
      secties: row.secties.map((section) => ({ sectieId: section.sectieId, snapshot: Uint8Array.from(atob(section.snapshot), (sign) => sign.charCodeAt(0)) })),
    }))
  })
}

/** De contextkop van deze instantie (R-125, R-128): wat `/voortgang` erover teruggeeft. */
export interface ContextInfo {
  readonly deadline?: string
  readonly dossierId?: string
  readonly uitvraagRef?: string
  /** De weergavenaam van het blok uit het image van de pod (ontwerp 9.5, R-125). */
  readonly bloknaam?: Text
  /** Het bouwblok als slug, voor de modus met historie (R-62, R-137). */
  readonly blok?: string
  /**
   * De weergavenamen van de blokken van buiten uit het image (R-58, ontwerp 9.5), onder de naam van de
   * prop van `FillForm`, want deze stand gaat als geheel naar het formulier. Een pod van daarvoor
   * stuurt ze niet, en dan toont het formulier de leesbare slug.
   */
  readonly bronNamen?: Readonly<Record<string, Text>>
}

/** Wat `/voortgang` als `bronnamen` stuurt: een naam in beide talen per blokaanduiding. */
const SourceNames = z.record(Block, Text)

/**
 * De contextkop-gegevens van deze instantie, of een lege `ContextInfo`.
 *
 * Niet fataal als het verzoek niet komt, om dezelfde reden als bij de werkverzameling en de
 * versies: het formulier hoort er ook zonder deze gegevens te staan, ontbrekend en mislukt zijn
 * voor deze schil hetzelfde geval (dezelfde reden als bij `haalPersoonVanWhoami` in
 * `contextstand.ts`). `dossierId` en `uitvraagRef` staan altijd op de instantie en komen dus mee
 * zodra het verzoek lukt; `deadline` is optioneel op de instantie zelf (R-78) en wordt tegen
 * `Moment` gevalideerd, want wat over de lijn komt is JSON en geen vertrouwde waarde totdat de
 * vorm is getoetst.
 */
export async function getContextInfo(context: FormContext): Promise<ContextInfo> {
  const url = `/voortgang?instantie=${encodeURIComponent(context.instantieId)}`
  return await readOptional<ContextInfo>(context, url, {}, (read) => {
    const body = read as {
      deadline?: unknown
      dossierId?: unknown
      uitvraagRef?: unknown
      bloknaam?: unknown
      blok?: unknown
      bronnamen?: unknown
    }
    const deadline = Moment.safeParse(body.deadline)
    const blockName = Text.safeParse(body.bloknaam)
    const sourceNames = SourceNames.safeParse(body.bronnamen)
    return {
      ...(deadline.success ? { deadline: deadline.data } : {}),
      ...(typeof body.dossierId === 'string' ? { dossierId: body.dossierId } : {}),
      ...(typeof body.uitvraagRef === 'string' ? { uitvraagRef: body.uitvraagRef } : {}),
      ...(blockName.success ? { bloknaam: blockName.data } : {}),
      ...(typeof body.blok === 'string' ? { blok: body.blok } : {}),
      ...(sourceNames.success ? { bronNamen: sourceNames.data } : {}),
    }
  })
}

/**
 * Eén eerdere versie, met haar antwoorden door de projectie van deze lezer (R-96, R-179).
 *
 * De pod filtert; de pagina doet dat niet en zou het ook niet kunnen. Wat hier gebeurt is het
 * antwoord per veld in een `Map` zetten, want het onderscheid tussen "stond er leeg in" en
 * "stond er niet in" moet blijven bestaan.
 *
 * **Het hele antwoord en niet `bevroren.waarde`.** Een bevroren antwoord is een van vier vormen
 * (`Antwoord` in `revisie.ts`): een enkele waarde, een verzameling waarden, documenten, of rijen.
 * Alleen de eerste heeft een `waarde`-sleutel. Tot deze correctie pakte de pagina die sleutel
 * uit, dus een meerkeuzeantwoord en een bijlage kwamen als `undefined` aan en de renderer meldde
 * "niet als tekst te tonen" terwijl de gegevens er waren. Een leesronde vond het. De renderer
 * kent de vier vormen en kiest wat er te tonen valt.
 */
export async function getVersie(
  context: FormContext,
  revisieId: string,
): Promise<ReadonlyMap<string, unknown> | undefined> {
  const url = `/revisie?instantie=${encodeURIComponent(context.instantieId)}&revisie=${encodeURIComponent(revisieId)}`
  return await readOptional<ReadonlyMap<string, unknown> | undefined>(context, url, undefined, (read) => {
    const body = read as { antwoorden?: Readonly<Record<string, unknown>> }
    const out = new Map<string, unknown>()
    for (const [fieldId, frozen] of Object.entries(body.antwoorden ?? {})) {
      out.set(fieldId, frozen)
    }
    return out
  })
}

/** Het lichaam van `GET /werkverzameling`, zoals de pod het levert. */
export interface WerkverzamelingBody {
  readonly werkverzameling: NonNullable<FormProps['werkverzameling']>
  readonly opmerkingen: NonNullable<FormProps['comments']>
  readonly gewijzigdeVelden: NonNullable<FormProps['changedFields']>
  readonly vorigeUitkomsten: NonNullable<FormProps['previousOutcomes']>
  /** De uitkomsten van déze beurt, geprojecteerd (M7.4). Voor de criterium-control. */
  readonly uitkomsten: readonly CriterionOutcome[]
}
